import React, { type ReactNode } from "react";
import { Text, Flex, Box, type Icon, AspectRatio, SimpleGrid } from "@chakra-ui/react";
import { Button } from "ui/button";
import { Diptych, type DiptychProps } from "ui/diptych";
import Link from "next/link";
import {
  ExperimentsIcon,
  LightningIcon,
  MegaphoneIcon,
  PlusIcon,
  RefreshIcon,
  SettingsIcon,
  TargetIcon,
  TrendIcon,
  WarehouseIcon,
} from "ui/icons";
import { integrationsCount } from "../../constants/integrations";

type UseCaseCardProps = {
  icon: { bgColor: string; color: string; icon: typeof Icon };
  title: string;
  href: string;
};

function UseCaseCard({ icon, title, href }: UseCaseCardProps) {
  return (
    <AspectRatio ratio={1 / 1} w="100%">
      <Flex
        as={Link}
        aria-label={title}
        href={href}
        target="_blank"
        boxSize="100%"
        borderRadius={{ base: "small", sm: "big", md: "small", xl: "big" }}
        border="1px"
        borderColor="brand.backgroundSecondary.light"
        bg="brand.backgroundSecondary.light"
        display="flex"
        flexDir="column"
        alignItems="center"
        gap={{ base: "tiny", sm: "smaller" }}
        justifyContent="end"
        textAlign="center"
        px="tinier"
        py="small"
        transition="all .15s ease-in-out"
        _hover={{
          bgColor: "brand.background.light",
          transform: { base: "none", md: "translateY(-20px)" },
        }}
        _focus={{ outline: "none" }}
        _focusVisible={{
          borderColor: "new.blue.950",
          boxShadow: "outline",
        }}
      >
        <Flex
          align="center"
          borderRadius={{ base: "6px", sm: "10px" }}
          bg={icon.bgColor}
          boxSize={{ base: "20px", sm: "40px", md: "32px", xl: "40px" }}
          justify="center"
          flexShrink={0}
          mt="auto"
        >
          <Box as={icon.icon} boxSize={{ base: "icon.xs", sm: "icon.md" }} color={icon.color} />
        </Flex>
        <Text
          color="new.gray.900"
          textStyle="caption2"
          fontSize={{ base: "8px", sm: "10px", xl: "11px" }}
          lineHeight={{ base: "12", sm: "16" }}
          fontWeight="semibold"
          whiteSpace="pre-wrap"
          mx="tiniest"
        >
          {title}
        </Text>
      </Flex>
    </AspectRatio>
  );
}

function CardGroup({ children }: { children: ReactNode }) {
  return (
    <Box pos="relative">
      <SimpleGrid columns={3} gap={{ base: "tiny", sm: "smaller", md: "tiny", xl: "smallest" }} w="100%" mx="tiniest">
        {children}
      </SimpleGrid>
    </Box>
  );
}

const diptychProps: Pick<DiptychProps, "titleAs" | "size" | "theme" | "gap" | "mediaRatio"> = {
  titleAs: "h3",
  size: "sm",
  theme: "dark",
  gap: { base: "bigger", sm: "biggest", md: "bigger", lg: "huge" },
  mediaRatio: { base: 5 / 2, md: 3 / 2 }, // Ratio should be taller than the actual cards to allow for hover interaction without overflow
};

export function UseCasesByTeam() {
  return (
    <Flex flexDir="column" gap="huger" mt={{ base: "huge", md: "hugest" }} mb="huge">
      <Diptych
        {...diptychProps}
        direction="right"
        title="Marketing teams"
        description="Use all of your organization’s data to power personalization and optimize campaigns with a complete and simple set of features."
        actions={
          <Button hasRightChevron href="/solutions/marketing">
            Hightouch for Marketing
          </Button>
        }
        media={
          <CardGroup>
            <UseCaseCard
              icon={{ bgColor: "new.blue.300", color: "new.blue.900", icon: MegaphoneIcon }}
              title={`Personalize \ncampaigns`}
              href="/playbooks/Product-Utilization-Emails-Iterable"
            />
            <UseCaseCard
              icon={{ bgColor: "new.green.300", color: "new.green.50", icon: ExperimentsIcon }}
              title={`Experiment across \nchannels`}
              href="/platform/customer-studio"
            />
            <UseCaseCard
              icon={{ bgColor: "new.violet.700", color: "new.violet.150", icon: TrendIcon }}
              title={`Understand campaign \nperformance`}
              href="/platform/campaign-intelligence"
            />
          </CardGroup>
        }
      />

      <Diptych
        {...diptychProps}
        direction="left"
        title="Advertising teams"
        description="Combine the power of first-party and third-party data onboarding in one tool to maximize revenue & ROAS."
        actions={
          <Button hasRightChevron href="/solutions/advertising">
            Hightouch for Advertising
          </Button>
        }
        media={
          <CardGroup>
            <UseCaseCard
              icon={{ bgColor: "new.yellow.300", color: "new.yellow.700", icon: LightningIcon }}
              title="Power conversion events"
              href="/playbooks/send-better-signals-with-facebook-conversion-api"
            />
            <UseCaseCard
              icon={{ bgColor: "new.blue.900", color: "new.blue.150", icon: TargetIcon }}
              title="Target & suppress audiences"
              href="/platform/customer-studio"
            />
            <UseCaseCard
              icon={{ bgColor: "new.red.500", color: "new.red.150", icon: PlusIcon }}
              title={`Increase\n match rates`}
              href="/platform/match-booster"
            />
          </CardGroup>
        }
      />

      <Diptych
        {...diptychProps}
        direction="right"
        title="Data teams"
        description={
          <>
            Sync data across your business with hundreds of prebuilt, resilient, low-latency integrations. No CSVs,
            in-house pipelines, or APIs required.
            <br />
            <br />
            Our developer-friendly tooling offers advanced features for governance, observability, alerting and change
            data capture.
          </>
        }
        actions={
          <Button hasRightChevron href="/solutions/data">
            Hightouch for Data Teams
          </Button>
        }
        media={
          <CardGroup>
            <UseCaseCard
              icon={{ bgColor: "new.blue.500", color: "new.blue.50", icon: RefreshIcon }}
              title={`Sync data with \nReverse ETL`}
              href="/platform/reverse-etl"
            />
            <UseCaseCard
              icon={{ bgColor: "new.violet.700", color: "new.violet.150", icon: WarehouseIcon }}
              title={`Turn your warehouse \ninto a CDP`}
              href="/blog/friends-dont-let-friends-buy-a-cdp"
            />
            <UseCaseCard
              icon={{ bgColor: "new.green.700", color: "new.green.50", icon: SettingsIcon }}
              title={`Integrate with \n${integrationsCount}+ tools`}
              href="/integrations"
            />
          </CardGroup>
        }
      />
    </Flex>
  );
}
